import { useEffect, useRef, useCallback } from 'react';
import { tokenStore } from '../utils/token.js';

const WS_BASE = import.meta.env.VITE_WS_URL || 'ws://localhost:8000';
const BACKOFF = [1000, 2000, 4000, 8000, 16000, 30000];

export function useWebSocket(path, onMessage, { enabled = true, onReconnect } = {}) {
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const timerRef = useRef(null);
  const onMessageRef = useRef(onMessage);
  const onReconnectRef = useRef(onReconnect);

  useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);
  useEffect(() => { onReconnectRef.current = onReconnect; }, [onReconnect]);

  const connect = useCallback(() => {
    if (!enabled) return;
    const token = tokenStore.get();
    if (!token) return;

    const ws = new WebSocket(`${WS_BASE}${path}`);
    wsRef.current = ws;

    ws.onopen = () => {
      retryRef.current = 0;
      ws.send(JSON.stringify({ type: 'auth', token }));
      if (onReconnectRef.current) onReconnectRef.current();
    };

    ws.onmessage = (event) => {
      try { onMessageRef.current?.(JSON.parse(event.data)); } catch { /* ignore */ }
    };

    ws.onclose = (event) => {
      if (!enabled || event.wasClean) return;
      const delay = BACKOFF[Math.min(retryRef.current, BACKOFF.length - 1)];
      retryRef.current += 1;
      timerRef.current = setTimeout(connect, delay);
    };

    ws.onerror = () => ws.close();
  }, [path, enabled]);

  useEffect(() => {
    if (!enabled) return;
    connect();
    return () => {
      clearTimeout(timerRef.current);
      wsRef.current?.close(1000, 'component unmounted');
    };
  }, [connect, enabled]);

  const send = useCallback((data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  return { send };
}
